import { faPlus, faTimesCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import { faFileExcel, faIndustry, faPlusCircle, faTags } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UIView, type UIViewInjectedProps } from '@uirouter/react';
import { Button, Modal, Space, Dropdown, Menu } from 'antd';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Table } from '@/components/table';
import { PERMISSION_CREATE, PERMISSION_DELETE, PERMISSION_OBJ_WAYBILL } from '@/constants/permissions';
import { withPermissions } from '@/hocs/permission';
import { waybillStore } from '@/stores';
import { getTranslatedString } from '@/utils';
import { showError } from '@/utils/common';

import { columns } from './setup';
import type { FilterValue, TableRowSelection } from 'antd/lib/table/interface';
import { api } from '@/api';
import { getGenericFilters, toQueryFormat } from '@/components/table/utils';

const entity = 'waybills';
const store = waybillStore;
const { confirm } = Modal;

function buildWaybillListParamsFromRouter(routerParams: {
  productName?: string | null;
  consortiumName?: string | null;
  weighingAnalysisStartDate?: string | null;
  weighingAnalysisEndDate?: string | null;
  missingCooperativeConsortium?: boolean | string | null;
}): cyrian.api.waybills.GetParams {
  const base: cyrian.api.waybills.GetParams = { sort: 'id:-1' };
  if (routerParams.productName) {
    base['product.name'] = routerParams.productName;
  }
  if (routerParams.consortiumName) {
    base['cooperativeConsortium.name'] = routerParams.consortiumName;
  }
  const m = routerParams.missingCooperativeConsortium;
  if (m === true || m === 'true' || m === '1') {
    base.missingCooperativeConsortium = true;
  }
  if (routerParams.weighingAnalysisStartDate && routerParams.weighingAnalysisEndDate) {
    base.weighingAnalysisStartDate = routerParams.weighingAnalysisStartDate;
    base.weighingAnalysisEndDate = routerParams.weighingAnalysisEndDate;
  }
  return base;
}

const CreateButton = withPermissions([[PERMISSION_OBJ_WAYBILL, PERMISSION_CREATE]])(
  ({ onClick, i18nTitle, disabled, icon }: any) => {
    return (
      <Button onClick={onClick} type="primary" icon={icon} disabled={disabled}>
        &nbsp;&nbsp;
        <FormattedMessage id={i18nTitle} />
      </Button>
    );
  },
);

const MassDeleteButton = withPermissions([[PERMISSION_OBJ_WAYBILL, PERMISSION_DELETE]])(
  ({ onClick, i18nTitle, disabled }: any) => {
    return (
      <Button
        onClick={onClick}
        type="primary"
        danger={true}
        disabled={disabled}
        icon={<FontAwesomeIcon icon={faTrash} />}
      >
        &nbsp;&nbsp;
        <FormattedMessage id={i18nTitle} />
      </Button>
    );
  },
);

export const Waybills = observer(({ transition }: UIViewInjectedProps) => {
  const [selectedWaybills, setSelectedWaybills] = useState<number[]>([]);
  const [tableResetKey, setTableResetKey] = useState(0);
  // Whether AntD Table has any column-level filter set. We can't read it
  // from Table state directly, so we maintain it from `onChange`.
  const [hasTableFilters, setHasTableFilters] = useState(false);

  const {
    productName,
    consortiumName,
    weighingAnalysisStartDate,
    weighingAnalysisEndDate,
    missingCooperativeConsortium,
  } = transition.router.globals.params as {
    productName?: string | null;
    consortiumName?: string | null;
    weighingAnalysisStartDate?: string | null;
    weighingAnalysisEndDate?: string | null;
    missingCooperativeConsortium?: boolean | string | null;
  };

  const stickyNavFilters = useMemo(
    () =>
      buildWaybillListParamsFromRouter({
        productName,
        consortiumName,
        weighingAnalysisStartDate,
        weighingAnalysisEndDate,
        missingCooperativeConsortium,
      }),
    [productName, consortiumName, weighingAnalysisStartDate, weighingAnalysisEndDate, missingCooperativeConsortium],
  );

  // URL-driven filters: read raw router params, not stickyNavFilters, so an
  // orphan date (only start or only end) — which buildWaybillListParamsFromRouter
  // drops — still counts as "URL is dirty" and keeps Clear All Filters reachable.
  const hasNavFilters = Boolean(
    productName ||
      consortiumName ||
      weighingAnalysisStartDate ||
      weighingAnalysisEndDate ||
      missingCooperativeConsortium,
  );
  const noActiveFilters = !hasNavFilters && !hasTableFilters;

  useEffect(() => {
    store.forceLoad(stickyNavFilters);
  }, [stickyNavFilters]);

  const items = store.items;

  const genericFilters = useMemo(() => getGenericFilters(columns), []);

  const onAdd = () => {
    transition.router.stateService.go(`base-layout.${entity}.create`);
  };

  const onMassAddProduct = () => {
    transition.router.stateService.go(`base-layout.${entity}.mass-add-product`, { selectedWaybills });
  };

  const onMassAddFactory = () => {
    transition.router.stateService.go(`base-layout.${entity}.mass-add-factory`, { selectedWaybills });
  };

  const onCreateFromExcel = () => {
    transition.router.stateService.go(`base-layout.${entity}.create-from-excel`);
  };

  const onAddLabAnalysesFromExcel = () => {
    transition.router.stateService.go(`base-layout.${entity}.add-lab-analyses-from-excel`);
  };

  const onLinkWithFarmersFromExcel = () => {
    transition.router.stateService.go(`base-layout.${entity}.link-with-farmers-from-excel`);
  };

  const onMassDelete = () => {
    const onOk = () =>
      api.waybills
        .bulkDelete({ ids: selectedWaybills })
        .source.then(() =>
          selectedWaybills.forEach((id: number) => {
            store.deleteItem(id);
          }),
        )
        .then(() => store.refresh())
        .then(() => setSelectedWaybills([]))
        .catch(showError);

    confirm({
      title: getTranslatedString('waybills.delete-warning-title'),
      content: getTranslatedString('waybills.delete-warning-desc'),
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk,
    });
  };

  const onRow = (record: cyrian.api.waybills.Waybill) => ({
    onClick: () => transition.router.stateService.go('base-layout.waybills.view', { waybillId: record.id }),
    className: 'table-row-cursor',
  });

  const onRowSelectionChange = (selectedRowKeys: number[], selectedLots: cyrian.api.waybills.Waybill[]) => {
    setSelectedWaybills(selectedRowKeys);
  };

  const rowSelection: TableRowSelection<cyrian.api.waybills.Waybill> = {
    preserveSelectedRowKeys: true,
    selectedRowKeys: selectedWaybills,
    onChange: onRowSelectionChange,
  };

  const onChangeTable = (...args) => {
    // args = [pagination, filters, sorter, extra]. AntD passes filters as
    // Record<columnKey, FilterValue | null>; a column is "active" when its
    // value is a non-empty array.
    const filters = args[1] as Record<string, FilterValue | null> | undefined;
    const tableFiltersActive = filters ? Object.values(filters).some((v) => Array.isArray(v) && v.length > 0) : false;
    setHasTableFilters(tableFiltersActive);

    const params = toQueryFormat.apply(null, [genericFilters, ...args]);
    store.forceLoad({ ...stickyNavFilters, ...params });
  };

  const onClearAllFilters = useCallback(() => {
    const trans = transition.router.stateService.go(
      'base-layout.waybills',
      {
        productName: null,
        consortiumName: null,
        weighingAnalysisStartDate: null,
        weighingAnalysisEndDate: null,
        missingCooperativeConsortium: null,
      },
      { inherit: false },
    );
    // 1) Bump key after transition settles so URL matches stickyNavFilters before Ant Table remounts.
    // 2) If go() is a no-op (same state/params — e.g. filters were only in the table), stickyNavFilters
    //    does not change, so useEffect will not reload the store; we must forceLoad + remount anyway.
    void Promise.resolve(trans).finally(() => {
      setTableResetKey((k) => k + 1);
      setHasTableFilters(false);
      if (trans.transition?.ignored?.()) {
        const params = transition.router.globals.params as {
          productName?: string | null;
          consortiumName?: string | null;
          weighingAnalysisStartDate?: string | null;
          weighingAnalysisEndDate?: string | null;
          missingCooperativeConsortium?: boolean | string | null;
        };
        store.forceLoad(buildWaybillListParamsFromRouter(params));
      }
    });
  }, [transition.router]);

  // Bulk PDF imports moved to a top-level sidebar entry under the Waybills
  // group — it's a persistent destination (queue with polling/retry/delete),
  // not a one-shot create-flow that fits this dropdown.
  const menu = (
    <Menu>
      <Menu.Item key="1" icon={<FontAwesomeIcon icon={faPlusCircle} />} onClick={onCreateFromExcel}>
        <FormattedMessage id={`${entity}.create-from-excel`} />
      </Menu.Item>
      <Menu.Item key="2" icon={<FontAwesomeIcon icon={faFileExcel} />} onClick={onAddLabAnalysesFromExcel}>
        <FormattedMessage id={`${entity}.lab-analyses.create-from-excel`} />
      </Menu.Item>
      <Menu.Item key="3" icon={<FontAwesomeIcon icon={faFileExcel} />} onClick={onLinkWithFarmersFromExcel}>
        <FormattedMessage id={`${entity}.link-with-farmers-from-excel`} />
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      <Space
        size={0}
        wrap
        align="center"
        style={{
          display: 'flex',
          width: '100%',
          marginBottom: 16,
          justifyContent: 'space-between',
        }}
      >
        <Space wrap>
          <Dropdown.Button
            type="primary"
            placement="bottomCenter"
            onClick={onAdd}
            overlay={menu}
            icon={<FontAwesomeIcon icon={faPlus} />}
          >
            <FormattedMessage id={`${entity}.add-new`} />
          </Dropdown.Button>
          <CreateButton
            onClick={onMassAddProduct}
            i18nTitle="waybills.mass-add-product"
            disabled={!selectedWaybills.length}
            icon={<FontAwesomeIcon icon={faTags} />}
          />
          <CreateButton
            onClick={onMassAddFactory}
            i18nTitle="waybills.mass-add-factory"
            disabled={!selectedWaybills.length}
            icon={<FontAwesomeIcon icon={faIndustry} />}
          />
          <MassDeleteButton
            onClick={onMassDelete}
            i18nTitle="waybills.mass-remove"
            disabled={!selectedWaybills.length}
          />
        </Space>
        <Button
          type="default"
          icon={<FontAwesomeIcon icon={faTimesCircle} />}
          onMouseDown={(e) => e.preventDefault()}
          onClick={onClearAllFilters}
          disabled={noActiveFilters}
        >
          &nbsp;&nbsp;
          <FormattedMessage id="waybills.clearAllFilters" />
        </Button>
      </Space>

      <Table
        key={tableResetKey}
        rowKey="id"
        columns={columns}
        dataSource={[...items]}
        store={store}
        scroll={{ x: 1600 }}
        onRow={onRow}
        rowSelection={rowSelection}
        onChange={onChangeTable}
      />
      <UIView />
    </>
  );
});
